import { useEffect, useRef, useState } from 'react';
import { Marker, Popup } from 'react-leaflet';
import Supercluster from 'supercluster';
import { point } from '@turf/helpers';
import { Icon } from 'leaflet';

// Create a custom cluster icon with dynamic sizing based on point count
const createClusterIcon = (count) => {
  // Augmenter la taille de base et l'échelle avec le nombre de points
  const baseSize = 30; // Taille de base plus grande
  const size = Math.min(baseSize + Math.sqrt(count) * 5, 100); // Échelle logarithmique pour une meilleure visibilité
  
  // Ajuster la taille de la police en fonction de la taille du cercle
  const fontSize = Math.min(24, Math.max(12, size * 0.4));
  
  return new Icon({
    iconUrl: `data:image/svg+xml;utf-8,<svg width="${size}" height="${size}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="50" r="45" fill="%2322c55e" fill-opacity="0.9" stroke="%23fff" stroke-width="4"/>
      <text x="50" y="50" font-size="${fontSize}" font-weight="bold" text-anchor="middle" fill="white" dy=".3em" font-family="Arial, sans-serif" stroke="%23000" stroke-width="1" stroke-opacity="0.5" paint-order="stroke">${count}</text>
    </svg>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    className: 'cluster-marker',
  });
};

const ClusterLayer = ({ points, zoom, bounds, onClusterClick, ...props }) => {
  const superclusterRef = useRef();
  const [clusters, setClusters] = useState([]);

  useEffect(() => {
    // Initialize supercluster avec un rayon de regroupement plus grand
    superclusterRef.current = new Supercluster({
      radius: 80, // Augmenté pour regrouper plus de points
      minPoints: 2, // Minimum de points pour former un cluster
      maxZoom: 16,  // Niveau de zoom maximal pour le clustering
      minZoom: 3,   // Niveau de zoom minimal pour le clustering
      extent: 256,  // Taille de la tuile (affecte la précision du regroupement)
      nodeSize: 64, // Taille du nœud pour l'optimisation des performances
    });
  }, []);

  useEffect(() => {
    if (points && points.length > 0) {
      // Convert points to GeoJSON features
      const features = points.map(point => ({
        type: 'Feature',
        properties: { id: point.id, ...point },
        geometry: {
          type: 'Point',
          coordinates: [point.longitude, point.latitude],
        },
      }));

      // Load points into supercluster
      superclusterRef.current.load(features);
      
      // Get clusters for current viewport
      if (bounds) {
        const bbox = [
          bounds.getWest(),
          bounds.getSouth(),
          bounds.getEast(),
          bounds.getNorth(),
        ];
        
        const newClusters = superclusterRef.current.getClusters(bbox, Math.floor(zoom));
        setClusters(newClusters);
      }
    }
  }, [points, zoom, bounds]);

  return (
    <>
      {clusters.map((cluster) => {
        const [longitude, latitude] = cluster.geometry.coordinates;
        const { cluster: isCluster, point_count: pointCount } = cluster.properties;
        
        if (isCluster) {
          // Render cluster marker
          return (
            <Marker
              key={`cluster-${cluster.id}`}
              position={[latitude, longitude]}
              icon={createClusterIcon(pointCount)}
              eventHandlers={{
                click: () => {
                  if (onClusterClick) {
                    onClusterClick(cluster);
                  }
                },
              }}
            >
              <Popup>
                <div className="cluster-popup">
                  <strong>{pointCount} AEDs</strong> in this area
                </div>
              </Popup>
            </Marker>
          );
        }
        
        // Render individual point
        const { id, name, address, accessibility } = cluster.properties;
        return (
          <Marker
            key={`point-${id}`}
            position={[latitude, longitude]}
            icon={props.icon}
          >
            <Popup>
              <div className="aed-popup">
                <h3 className="font-bold text-base mb-1">{name || 'AED Location'}</h3>
                {address && <p className="text-gray-700 mb-1">{address}</p>}
                {accessibility && (
                  <p className="text-gray-600 mb-1 text-xs">
                    Access: {accessibility}
                  </p>
                )}
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};

export default ClusterLayer;
